import { IAddress, ICard, IProfile, IUser } from '@/types'
import { sellerTypes } from 'constants/'
import { Err, getApiBasepath } from '.'

export type SellerType = keyof typeof sellerTypes

export type UserProfile = { id: number } & IProfile['attributes'] // This might need to be updated if the backend changes

type GetUserProfileResponse = {
  error?: Err
  message?: string
  profile?: UserProfile
}

type UpdateUserProfileResponse = {
  error?: Err
  message?: string
  profile?: UserProfile
}

type UpdateUserProfileCurrencyResponse = {
  error?: Err
  data?: UserProfile
}

type HasUserLiveListingsOrOffers = {
  error?: Err
  data?: { hasListingOffersLive: boolean }
}

type SetupExpressPaymentResponse = {
  error?: Err
  message?: string
  redirectUrl?: string
}

export type Profile = {
  firstName?: string
  lastName?: string
  mobilePhone?: string
  recipientName?: string
  communication?: boolean
  bidNewLowestAsk?: boolean
  bidNewHighestBid?: boolean
  bidNewSale?: boolean
  askNewLowestAsk?: boolean
  askNewHighestBid?: boolean
  askNewSale?: boolean
  followArtistNewLowestAsk?: boolean
  followArtistNewHighestBid?: boolean
  followArtistNewSale?: boolean
  saveArtworkNewLowestAsk?: boolean
  saveArtworkNewHighestBid?: boolean
  saveArtworkNewSale?: boolean
  user?: { data?: IUser }
  connectAccountId?: string
  customerAccountId?: string
  last4?: string
  shippingAddress?: Partial<IAddress>
  billingAddress?: Partial<IAddress>
  buyingCard?: Partial<ICard>
  sellerType?: SellerType
}

export const getUserProfile = async (
  userId: number,
  query?: string,
  isAuthenticated = false
) => {
  if (isAuthenticated === false) {
    return {
      error: {
        name: 'Auth',
        message: 'Not authenticated',
        status: 401,
      },
    }
  }

  const response = await fetch(
    `${getApiBasepath()}/api/profiles/${userId}?${query}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  const data = await response.json()
  return data as GetUserProfileResponse
}

export const updateUserProfile = async (
  userId: number,
  newProfile: Profile,
  query?: string
) => {
  const response = await fetch(
    `${getApiBasepath()}/api/profiles/${userId}?${query}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(newProfile),
    }
  )
  const data = await response.json()
  return data as UpdateUserProfileResponse
}

export const updateProfileCurrency = async (
  userId: number,
  currency: string
) => {
  const response = await fetch(
    `${getApiBasepath()}/api/profiles/update/currency/${userId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ currency }),
    }
  )
  const data = await response.json()
  return data as UpdateUserProfileCurrencyResponse
}

export const hasUserLiveListingsOrOffers = async () => {
  const response = await fetch(
    `${getApiBasepath()}/api/profiles/has-listings-offers-live`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  const data = await response.json()
  return data as HasUserLiveListingsOrOffers
}

export const updateUserProfilePreferences = async (
  newProfile: Profile,
  query?: string
) => {
  const response = await fetch(
    `${getApiBasepath()}/api/profiles/update/user/preferences?${query}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(newProfile),
    }
  )
  const data = await response.json()
  return data as UpdateUserProfileResponse
}

export const getClientSecret = async () => {
  const response = await fetch(`${getApiBasepath()}/api/profiles/payment`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  })
  const data = await response.json()
  return data as { clientSecret: string }
}

export const setupExpressPayment = async (
  refreshUrl: string,
  returnUrl: string
) => {
  const response = await fetch(`${getApiBasepath()}/api/profiles/express`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    body: JSON.stringify({ refreshUrl, returnUrl }),
  })
  const data = await response.json()
  return data as SetupExpressPaymentResponse
}

export const updateReleaseNotifications = async (payload: {
  timeZone: string
  notification1: number
  notification2: number
}) => {
  const response = await fetch(
    `${getApiBasepath()}/api/profiles/update/user/release`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(payload),
    }
  )
  const data = await response.json()
  return data as { error?: Err; success?: boolean }
}

export const saveLastActive = async () => {
  const response = await fetch(`${getApiBasepath()}/api/profiles/leave`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  })
  const data = await response.json()
  return data as { error?: Err; success?: boolean }
}

export const updateShippingAddress = async (
  userId: number,
  address: Partial<IAddress>
) => {
  const response = await fetch(
    `${getApiBasepath()}/api/profiles/update/shipping-address/${userId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ shippingAddress: address }),
    }
  )
  const data = await response.json()
  return data as { error?: Err; success?: boolean }
}

export interface UpdateOnboardingPayload {
  status?: string
  experience?: string
  intent?: string
}

export const updateOnboarding = async (
  userId: number,
  payload: UpdateOnboardingPayload
) => {
  const res = await fetch(
    `${getApiBasepath()}/api/profiles/update/onboarding/${userId}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  const data = await res.json()

  return data
}

interface UpdateUserTrackingFields {
  signUpArtworkId?: number
  signUpArtistId?: number
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmLandingPage?: string
}

export const updateTrackingFields = async (
  userId: number,
  payload: UpdateUserTrackingFields
) => {
  const res = await fetch(
    `${getApiBasepath()}/api/profiles/update/user-entry/${userId}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  const data = await res.json()

  return data as { success: boolean; errMessage: string }
}
